/**
 * Custom hook for managing comment actions
 * Responsibility: Comment creation and file upload handling
 */

import React from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import toast from "react-hot-toast";

import { useSession } from "@/providers/SessionProvider";
import { realtorCommentsService } from "@/services/realtor-comments.service";
import { createLogger } from "@/utils/logger";

const logger = createLogger("PropertyComments:useCommentActions");

export interface UseCommentActionsProps {
  /** Property ID for comment creation */
  propertyId: string;
  /** Parent comment ID for replies */
  parentId?: string;
  /** Optional callback for sending messages */
  onSendMessage?: (message: string) => void;
  /** Optional callback for file uploads */
  onUploadFile?: (file: File) => void;
}

export const useCommentActions = ({
  propertyId,
  parentId,
  onSendMessage,
  onUploadFile,
}: UseCommentActionsProps) => {
  const [message, setMessage] = React.useState<string>("");
  const [pendingFiles, setPendingFiles] = React.useState<File[]>([]);
  const [pendingImages, setPendingImages] = React.useState<File[]>([]);
  const queryClient = useQueryClient();
  const t = useTranslations();
  const { user, isAuthenticated } = useSession();

  // Create comment mutation with reply support
  const createMutation = useMutation({
    mutationFn: async (payload: {
      content: string;
      parentId?: string;
      attachments?: File[];
      images?: File[];
      userId?: string;
    }) => {
      // Check authentication state before attempting comment creation
      const userId = user?._id || user?.id;
      if (!isAuthenticated || !userId) {
        throw new Error("User authentication required to create comments");
      }

      // Use authenticated user ID if not provided in payload
      const userIdToUse = payload.userId || userId;
      if (!userIdToUse) {
        throw new Error("User authentication required to create comments");
      }

      const finalPayload = {
        ...payload,
        userId: userIdToUse,
        visibility: "private",
      };
      return realtorCommentsService.create(propertyId, finalPayload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["property-comments", "list"],
      });
      setMessage(""); // Clear input after successful creation
      setPendingFiles([]); // Clear pending files
      setPendingImages([]); // Clear pending images
      toast.success(t("Comment added successfully"));
    },
    onError: (error) => {
      logger.error("DEBUG: Failed to create comment:", error);
      toast.error(t("Failed to add comment. Please try again."));
    },
  });

  /**
   * Handle sending a message with pending files
   * Following Single Responsibility Principle - only handles message sending
   */
  const handleSendMessage = (): void => {
    const trimmed = message.trim();
    const hasFiles = pendingFiles.length > 0 || pendingImages.length > 0;

    // Require either message or files
    if (!trimmed && !hasFiles) return;

    if (onSendMessage) {
      onSendMessage(trimmed);
      setMessage("");
      setPendingFiles([]);
      setPendingImages([]);
      return;
    }

    const userId = user?._id || user?.id;

    // Check authentication before creating comment
    if (!isAuthenticated || !userId) {
      logger.error("Authentication failed - user not logged in");
      toast.error(t("Please sign in to leave a comment"));
      return;
    }

    // Use API directly with reply support and files
    createMutation.mutate({
      content:
        trimmed ||
        (hasFiles
          ? `Uploaded ${pendingFiles.length} file(s) and ${pendingImages.length} image(s)`
          : ""),
      parentId: parentId, // This will be undefined for top-level comments
      attachments: pendingFiles.length > 0 ? pendingFiles : undefined,
      images: pendingImages.length > 0 ? pendingImages : undefined,
      userId: userId, // Explicitly pass the authenticated user ID
    });

    // Note: State will be cleared in onSuccess callback to avoid premature clearing
  };

  /**
   * Handle file selection - no automatic upload
   */
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (onUploadFile) {
      onUploadFile(file);
    } else {
      // Add to pending files instead of immediate upload
      setPendingFiles((prev) => [...prev, file]);
    }

    // Clear the input to allow selecting the same file again
    e.target.value = "";
  };

  /**
   * Handle image selection - no automatic upload
   */
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length === 0) return;

    if (onUploadFile && imageFiles.length > 0) {
      imageFiles.forEach((file) => onUploadFile(file));
    } else {
      // Add to pending images instead of immediate upload
      setPendingImages((prev) => [...prev, ...imageFiles]);
    }

    // Clear the input to allow selecting the same files again
    e.target.value = "";
  };

  /**
   * Remove a pending file
   */
  const removePendingFile = (index: number): void => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Remove a pending image
   */
  const removePendingImage = (index: number): void => {
    setPendingImages((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Clear all pending files and images
   */
  const clearPendingFiles = (): void => {
    setPendingFiles([]);
    setPendingImages([]);
  };

  /**
   * Handle creating a reply to a comment
   */
  const handleCreateReply = (content: string, parentId: string): void => {
    const userId = user?._id || user?.id;
    // Check authentication before creating reply
    if (!isAuthenticated || !userId) {
      toast.error(t("Please sign in to leave a comment"));
      return;
    }

    createMutation.mutate({
      content,
      parentId,
      attachments: undefined,
      images: undefined,
      userId: userId, // Explicitly pass the authenticated user ID
    });
  };

  return {
    message,
    setMessage,
    handleSendMessage,
    handleFileUpload,
    handleImageUpload,
    pendingFiles,
    pendingImages,
    removePendingFile,
    removePendingImage,
    clearPendingFiles,
    handleCreateReply,
    isCreating: createMutation.isPending,
    createError: createMutation.error,
  };
};
